/**
 * Booking Provider Abstraction
 * Supports multiple booking systems: GHL+CaaS, Cal.com, and custom implementations
 */

import {
  BookingConfig,
  BookingData,
  BookingResponse,
  GHLCAASBookingConfig,
  CalComBookingConfig,
  CustomBookingConfig,
} from '../config/types';
import {
  preBookAppointment,
  getAvailableSlots,
  formatPhoneForAPI,
  formatAppointmentDate,
  type FreeSlotsResponse,
} from './caas-api';

/**
 * Base booking provider interface
 */
export interface BookingProvider {
  getAvailableSlots(
    startDate: number,
    endDate: number,
    timezone?: string,
    province?: string
  ): Promise<FreeSlotsResponse>;
  
  bookAppointment(data: BookingData): Promise<BookingResponse>;
}

/**
 * GHL + CaaS Booking Provider
 */
class GHLCAASBookingProvider implements BookingProvider {
  private config: GHLCAASBookingConfig;

  constructor(config: GHLCAASBookingConfig) {
    this.config = config;
  }

  async getAvailableSlots(
    startDate: number,
    endDate: number,
    timezone?: string,
    province?: string
  ): Promise<FreeSlotsResponse> {
    return getAvailableSlots(
      this.config.agencyId,
      startDate,
      endDate,
      timezone || this.config.timezone
    );
  }

  async bookAppointment(data: BookingData): Promise<BookingResponse> {
    try {
      const formattedPhone = formatPhoneForAPI(data.phone);
      const appointmentDate = formatAppointmentDate(
        new Date(data.appointmentDate),
        undefined,
        data.timezone || this.config.timezone
      );

      const response = await preBookAppointment({
        agencyId: this.config.agencyId,
        appointmentData: {
          prospect_name: `${data.firstName} ${data.lastName}`,
          prospect_email: data.email,
          prospect_phone: formattedPhone,
          appointment_date: appointmentDate,
          contact_id: data.contactId,
          notes: data.notes,
        },
      });

      return {
        success: true,
        appointmentId: response.appointmentId,
        bookingId: response.appointmentId,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to book appointment',
      };
    }
  }
}

/**
 * Cal.com Booking Provider
 */
class CalComBookingProvider implements BookingProvider {
  private config: CalComBookingConfig;
  private baseUrl: string;

  constructor(config: CalComBookingConfig) {
    this.config = config;
    // Support both v1 and v2 API
    const apiVersion = config.apiVersion || 'v1';
    this.baseUrl = `https://api.cal.com/${apiVersion}`;
  }

  /**
   * Get event type ID based on province from quiz data
   */
  private getEventTypeId(province?: string): string {
    if (province && this.config.provinceEventTypeMap) {
      const eventTypeId = this.config.provinceEventTypeMap[province];
      if (eventTypeId) {
        return eventTypeId;
      }
    }
    
    // Fall back to default event type ID
    if (this.config.calcomEventTypeId) {
      return this.config.calcomEventTypeId;
    }
    
    throw new Error('No Cal.com event type ID configured. Provide calcomEventTypeId or provinceEventTypeMap.');
  }

  async getAvailableSlots(
    startDate: number,
    endDate: number,
    timezone?: string,
    province?: string
  ): Promise<FreeSlotsResponse> {
    const eventTypeId = this.getEventTypeId(province);
    
    // Cal.com API for getting available slots
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.config.calcomApiKey}`,
      'Content-Type': 'application/json',
    };
    
    // Cal.com v2 slots endpoint requires cal-api-version: 2024-09-04
    if (this.config.apiVersion === 'v2') {
      headers['cal-api-version'] = '2024-09-04';
    }

    // Cal.com v2: start and end must be ISO 8601 date strings in UTC.
    // Date-only (YYYY-MM-DD) defaults to start/end of day; full ISO also accepted.
    const startStr = new Date(startDate).toISOString().slice(0, 10); // YYYY-MM-DD
    const endStr = new Date(endDate).toISOString().slice(0, 10);       // YYYY-MM-DD

    const params = new URLSearchParams({
      eventTypeId: eventTypeId,
      start: startStr,
      end: endStr,
    });
    if (timezone) params.append('timeZone', timezone);

    const response = await fetch(
      `${this.baseUrl}/slots?${params.toString()}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Failed to fetch Cal.com slots: ${response.status} ${errText}`);
    }

    const data = await response.json();

    // Cal.com v2 response: { status: "success", data: { "YYYY-MM-DD": [{ start: "..." }], ... } }
    const slotsData = data.data ?? data.slots ?? {};

    return {
      agencyId: eventTypeId,
      freeSlots: slotsData,
    };
  }

  async bookAppointment(data: BookingData): Promise<BookingResponse> {
    try {
      const eventTypeId = this.getEventTypeId(data.quizData?.province);
      
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${this.config.calcomApiKey}`,
        'Content-Type': 'application/json',
      };
      
      // Add API version header for v2 (must be 2024-08-13 per Cal.com docs)
      if (this.config.apiVersion === 'v2') {
        headers['cal-api-version'] = '2024-08-13';
      }

      // Build payload based on API version
      let payload: any;
      if (this.config.apiVersion === 'v2') {
        // V2 API uses attendee object
        payload = {
          eventTypeId: parseInt(eventTypeId),
          start: data.appointmentDate,
          attendee: {
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            timeZone: data.timezone || 'UTC',
            ...(data.phone && { phoneNumber: data.phone }),
          },
        };
      } else {
        // V1 API uses responses object
        payload = {
          eventTypeId: eventTypeId,
          start: data.appointmentDate,
          responses: {
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            phone: data.phone,
          },
          metadata: {
            notes: data.notes,
            province: data.quizData?.province,
          },
        };
      }
      
      const response = await fetch(`${this.baseUrl}/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errMsg = `Cal.com booking failed (${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody?.message) errMsg = errBody.message;
        } catch {
          const text = await response.text();
          if (text) errMsg = text.slice(0, 200);
        }
        throw new Error(errMsg);
      }

      const res = await response.json();
      // Cal.com v2: { status: "success", data: { id, uid, meetingUrl, ... } }
      const booking = res.data ?? res;

      // Trigger webhook if configured
      if (this.config.webhookUrl) {
        try {
          await fetch(this.config.webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              bookingId: booking.id ?? booking.uid,
              ...data,
            }),
          });
        } catch (webhookError) {
          console.error('Webhook error (non-fatal):', webhookError);
        }
      }

      return {
        success: true,
        appointmentId: booking.id ?? booking.uid,
        bookingId: booking.id ?? booking.uid,
        confirmationUrl: booking.meetingUrl ?? booking.confirmationUrl,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to book Cal.com appointment',
      };
    }
  }
}

/**
 * Custom Booking Provider
 */
class CustomBookingProvider implements BookingProvider {
  private config: CustomBookingConfig;

  constructor(config: CustomBookingConfig) {
    this.config = config;
  }

  async getAvailableSlots(
    startDate: number,
    endDate: number,
    timezone?: string,
    province?: string
  ): Promise<FreeSlotsResponse> {
    // Call custom API for slots
    const params = new URLSearchParams({
      startDate: startDate.toString(),
      endDate: endDate.toString(),
    });
    if (timezone) params.append('timezone', timezone);
    if (province) params.append('province', province);
    
    const response = await fetch(
      `${this.config.apiUrl}/slots?${params.toString()}`,
      {
        headers: {
          ...(this.config.apiKey && { Authorization: `Bearer ${this.config.apiKey}` }),
          ...this.config.headers,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch custom booking slots');
    }

    return response.json();
  }

  async bookAppointment(data: BookingData): Promise<BookingResponse> {
    return this.config.bookAppointment(data);
  }
}

/**
 * Create a booking provider from config
 */
export function createBookingProvider(config: BookingConfig): BookingProvider {
  switch (config.type) {
    case 'ghl-caas':
      return new GHLCAASBookingProvider(config);
    case 'calcom':
      return new CalComBookingProvider(config);
    case 'custom':
      return new CustomBookingProvider(config);
    default:
      throw new Error(`Unsupported booking provider type: ${(config as any).type}`);
  }
}
